import { useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import type { RealtimeEventOfType } from 'fym-shared/src/music24/realtimeEvents';
import { useTRPCClientAPI } from '../../../../trpc/trpc';
import { useRealtimeEvent } from '../../../realtime/useRealtimeEvent';
import { useShowToast } from '../../../../redux/slices/toasts/hooks';
import { registerToastAction } from '../../../../redux/slices/toasts/toastActions';
import { ToastType } from '../../../../redux/slices/toasts/state';
import { outreachReplyPath } from '../outreachPaths';
import type { OutreachActivityPage } from '../outreachTypes';
import { liveMessages as messages } from '../live/liveMessages';
import { replyLabelText } from '../live/replyLabelText';

/**
 * The action id of "Read and answer" in the toast of one reply. Each toast has its own id, so the
 * button of an older toast opens its own reply, not the newest one.
 */
export function outreachReplyToastAction(replyUuid: string): string {
  return `outreach.reply.open.${replyUuid}`;
}

/**
 * The reply toast of the live campaign (design t2): on `outreach.reply.received` of this artist
 * (and of this campaign, when one is given) it reads the reply's activity line (the event holds
 * ids only) and shows the curator, the label and the first words, with "Read and answer".
 */
export function useReplyToast(accountId: number, campaignUuid: string | null): void {
  const intl = useIntl();
  const navigate = useNavigate();
  const trpcClient = useTRPCClientAPI();
  const { show } = useShowToast();
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  /** The remove functions of the toast actions that this hook registered, by reply uuid. */
  const actions = useRef(new Map<string, () => void>());

  useEffect(() => {
    const registered = actions.current;
    return () => {
      registered.forEach((remove) => remove());
      registered.clear();
    };
  }, []);

  /** Registers "Read and answer" of one reply: it opens that reply. */
  const registerOpen = useCallback((replyUuid: string): string => {
    const actionId = outreachReplyToastAction(replyUuid);
    if (!actions.current.has(replyUuid)) {
      actions.current.set(
        replyUuid,
        registerToastAction(actionId, () => {
          // eslint-disable-next-line no-void
          void navigateRef.current(outreachReplyPath(replyUuid));
        })
      );
    }
    return actionId;
  }, []);

  const onReply = useCallback(
    (event: RealtimeEventOfType<'outreach.reply.received'>) => {
      if (event.accountId !== accountId || (campaignUuid !== null && event.campaignUuid !== campaignUuid)) {
        return;
      }
      (
        trpcClient.outreach.campaigns.activity.query({
          accountId,
          campaignUuid: event.campaignUuid,
          limit: 10,
        }) as Promise<OutreachActivityPage>
      )
        .then((page) => {
          const line = page.events.find((item) => item.kind === 'replied' && item.replyUuid === event.replyUuid);
          // A reply that is not a human reply (out of office, bounce) has no line: no toast.
          if (line === undefined) {
            return;
          }
          const actionId = registerOpen(event.replyUuid);
          show({
            type: ToastType.success,
            uniqueId: `outreach.reply.${event.replyUuid}`,
            title: intl.formatMessage(messages.replyToastTitle, {
              name: line.who?.name ?? '',
              label: intl.formatMessage(replyLabelText(line.replyLabel)),
            }),
            description: line.snippet === null ? '' : intl.formatMessage(messages.quoted, { text: line.snippet }),
            autoHide: false,
            action: { label: intl.formatMessage(messages.replyToastAction), actionId },
          });
        })
        .catch(console.error);
    },
    [accountId, campaignUuid, intl, registerOpen, show, trpcClient]
  );
  useRealtimeEvent('outreach.reply.received', onReply);
}
